import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getOrderGenieErpPricesBulk,
  getOrderGenieProductMatchEngine,
  getOrderGenieSettings,
  testOrderGenieNotificationEmail,
  testOrderGenieNotificationSms,
  updateOrderGenieSettings,
} from '../services/orderXApi';
import { OrderGenieEmailTemplateEditor } from '../components/OrderXEmailTemplateEditor/OrderXEmailTemplateEditor';
import OrderGenieReadiness from './OrderXReadiness';
import MailboxConfigForm from '../components/MailboxConfig/MailboxConfigForm';
import CheckoutFlowAdminSection from '../components/OrderXV4/CheckoutFlowAdminSection';
import { DEFAULT_CHECKOUT_FLOW_CONFIG } from './orderXV4Utils';

const DEFAULT_FORM = {
  maxConcurrentRequestsPerAccount: 3,
  globalActiveRequestLimit: 3,
  queuePriorityEnabled: true,
  queueDispatchMode: 'local_task',
  queueChannel: 'local',
  queueWeightUrgent: 1,
  queueWeightNormal: 5,
  queueWeightLow: 9,
  queueAdminUsersText: '',
  tabularOnlyModeEnabled: true,
  tabularOnlyCompletenessThreshold: 0.95,
  extractionAiCallTimeoutSec: 60,
  extractionAiStageBudgetSec: 180,
  parsingStallWatchdogSeconds: 240,
  tabularPreprocessEnabled: true,
  tabularPreprocessSampleRows: 30,
  productMatchEngine: 'solr',
  productMatchShadow: false,
  pricingEngine: 'dummy',
  eclipseShiptoUrl: '',
  erpMatchEnabled: false,
  notificationEnabled: false,
  notificationChannel: 'email',
  notificationReviewBaseUrl: '',
  notificationDefaultEmailsText: '',
  notificationDefaultPhonesText: '',
  smtpHost: '',
  smtpPort: 587,
  smtpUsername: '',
  smtpPassword: '',
  smtpUseTls: true,
  smtpFromEmail: '',
  twilioAccountSid: '',
  twilioAuthToken: '',
  twilioFromNumber: '',
  notificationEmailSubjectTemplate: '',
  notificationEmailBodyHtmlTemplate: '',
  notificationEmailBodyTextTemplate: '',
  notificationEmailByStatus: {},
  notificationSmsBodyTemplate: '',
};

const normalizeInt = (value, fallback, min, max) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(min, Math.min(max, Math.round(parsed)));
};

const normalizeFloat = (value, fallback, min, max) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(min, Math.min(max, parsed));
};

const parseAdminUsers = (textValue) => {
  const raw = String(textValue || '');
  const tokens = raw
    .split(/[\n,]/g)
    .map((entry) => String(entry || '').trim().toLowerCase())
    .filter(Boolean);
  return Array.from(new Set(tokens));
};

const parseCommaLines = (textValue) => {
  const raw = String(textValue || '');
  const tokens = raw
    .split(/[\n,]/g)
    .map((entry) => String(entry || '').trim())
    .filter(Boolean);
  return Array.from(new Set(tokens));
};

const toAdminUsersText = (users) => {
  if (!Array.isArray(users)) return '';
  return users.map((entry) => String(entry || '').trim()).filter(Boolean).join('\n');
};

const SECTION_ITEMS = [
  { id: 'capacity', label: 'Queue Capacity', help: 'Concurrent processing limits per account.' },
  { id: 'routing', label: 'Queue Dispatch', help: 'How jobs are dispatched to workers.' },
  { id: 'search', label: 'Product Matching Search', help: 'Select Solr/OpenSearch engine for product matching.' },
  { id: 'priority', label: 'Priority Policy', help: 'Enable priorities and set queue weights.' },
  { id: 'access', label: 'Admin Access', help: 'User IDs allowed to set urgent priority.' },
  { id: 'extraction', label: 'Extraction Reliability', help: 'Controls for tabular-first and AI timeout budgets.' },
  { id: 'notifications', label: 'Notifications', help: 'Alerts when requests are created (review link + summary).' },
  { id: 'checkout-flow', label: 'Checkout Flow', help: 'Configure OrderX V4 fields, valid values, and conditional rules.' },
  { id: 'mailbox', label: 'Mailbox Setup', help: 'Configure IMAP mailbox sync and sender security.' },
  { id: 'readiness', label: 'Readiness', help: 'Run readiness checks, setup wizard, and go-live gate.' },
];

const NOTIFICATION_TEMPLATE_HINTS = [
  { token: '{{review_url}}', label: 'Review link' },
  { token: '{{request_name}}', label: 'Request title' },
  { token: '{{summary}}', label: 'Summary text' },
  { token: '{{status}}', label: 'Status' },
  { token: '{{user_id}}', label: 'User id' },
  { token: '{{request_id}}', label: 'Request id' },
];

const NOTIFICATION_SUB_TABS = [
  { id: 'setup', label: 'Setup' },
  { id: 'email-template', label: 'Email template' },
  { id: 'sms-template', label: 'SMS template' },
  { id: 'test', label: 'Test delivery' },
];

function normalizeEmailByStatusSettings(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const out = {};
  for (const [k, v] of Object.entries(raw)) {
    const key = String(k).trim().toLowerCase();
    if (!key || !v || typeof v !== 'object') continue;
    out[key] = {
      subject: String(v.subject ?? ''),
      html: String(v.html ?? ''),
      text: String(v.text ?? ''),
    };
  }
  return out;
}

function compactNotificationEmailByStatusForApi(obj) {
  const out = {};
  for (const [k, v] of Object.entries(obj || {})) {
    if (!v || typeof v !== 'object') continue;
    const row = {};
    if (String(v.subject || '').trim()) row.subject = String(v.subject).trim();
    if (String(v.html || '').trim()) row.html = String(v.html).trim();
    if (String(v.text || '').trim()) row.text = String(v.text).trim();
    if (Object.keys(row).length) out[String(k).toLowerCase()] = row;
  }
  return out;
}

function buildFormStateFromSettings(settings) {
  const queueWeights = settings?.queue_weights && typeof settings.queue_weights === 'object'
    ? settings.queue_weights
    : {};
  return {
    maxConcurrentRequestsPerAccount: normalizeInt(
      settings?.max_concurrent_requests_per_account,
      3,
      1,
      20
    ),
    globalActiveRequestLimit: normalizeInt(settings?.global_active_request_limit, 3, 1, 20),
    queuePriorityEnabled: Boolean(settings?.queue_priority_enabled ?? true),
    queueDispatchMode: String(settings?.queue_dispatch_mode || 'local_task'),
    queueChannel: String(settings?.queue_channel || 'local'),
    queueWeightUrgent: normalizeInt(queueWeights?.urgent, 1, 1, 9),
    queueWeightNormal: normalizeInt(queueWeights?.normal, 5, 1, 9),
    queueWeightLow: normalizeInt(queueWeights?.low, 9, 1, 9),
    queueAdminUsersText: toAdminUsersText(settings?.queue_admin_users),
    tabularOnlyModeEnabled: Boolean(settings?.tabular_only_mode_enabled ?? true),
    tabularOnlyCompletenessThreshold: normalizeFloat(
      settings?.tabular_only_completeness_threshold,
      0.95,
      0.5,
      1
    ),
    extractionAiCallTimeoutSec: normalizeInt(settings?.extraction_ai_call_timeout_sec, 60, 10, 300),
    extractionAiStageBudgetSec: normalizeInt(settings?.extraction_ai_stage_budget_sec, 180, 30, 1200),
    parsingStallWatchdogSeconds: normalizeInt(settings?.parsing_stall_watchdog_seconds, 240, 30, 1800),
    tabularPreprocessEnabled: Boolean(settings?.tabular_preprocess_enabled ?? true),
    tabularPreprocessSampleRows: normalizeInt(settings?.tabular_preprocess_sample_rows, 30, 10, 100),
    productMatchEngine: String(settings?.product_match_engine || 'solr').toLowerCase() === 'opensearch'
      ? 'opensearch'
      : (String(settings?.product_match_engine || 'solr').toLowerCase() === 'shadow' ? 'shadow' : 'solr'),
    productMatchShadow: Boolean(settings?.product_match_shadow ?? false),
    // Single selector covers both backend settings: eclipse is stored as
    // erp_connector=eclipse + pricing_engine=eclipse; dummy/p21 come from pricing_engine.
    pricingEngine: String(settings?.erp_connector || '').toLowerCase() === 'eclipse'
      ? 'eclipse'
      : (String(settings?.pricing_engine || 'dummy').toLowerCase() === 'p21' ? 'p21' : 'dummy'),
    eclipseShiptoUrl: String(settings?.eclipse_shipto_url || ''),
    erpMatchEnabled: Boolean(settings?.erp_match_enabled ?? false),
    notificationEnabled: Boolean(settings?.notification_enabled ?? false),
    notificationChannel: ['email', 'sms', 'both'].includes(String(settings?.notification_channel || '').toLowerCase())
      ? String(settings?.notification_channel || 'email').toLowerCase()
      : 'email',
    notificationReviewBaseUrl: String(settings?.notification_review_base_url || ''),
    notificationDefaultEmailsText: Array.isArray(settings?.notification_default_emails)
      ? settings.notification_default_emails.join('\n')
      : '',
    notificationDefaultPhonesText: Array.isArray(settings?.notification_default_phones)
      ? settings.notification_default_phones.join('\n')
      : '',
    smtpHost: String(settings?.smtp_host || ''),
    smtpPort: normalizeInt(settings?.smtp_port, 587, 1, 65535),
    smtpUsername: String(settings?.smtp_username || ''),
    smtpPassword: String(settings?.smtp_password || ''),
    smtpUseTls: settings?.smtp_use_tls !== false,
    smtpFromEmail: String(settings?.smtp_from_email || ''),
    twilioAccountSid: String(settings?.twilio_account_sid || ''),
    twilioAuthToken: String(settings?.twilio_auth_token || ''),
    twilioFromNumber: String(settings?.twilio_from_number || ''),
    notificationEmailSubjectTemplate: String(settings?.notification_email_subject_template || ''),
    notificationEmailBodyHtmlTemplate: String(settings?.notification_email_body_html_template || ''),
    notificationEmailBodyTextTemplate: String(settings?.notification_email_body_text_template || ''),
    notificationEmailByStatus: normalizeEmailByStatusSettings(settings?.notification_email_by_status),
    notificationSmsBodyTemplate: String(settings?.notification_sms_body_template || ''),
  };
}

const OrderGenieAccountAdmin = () => {
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('capacity');
  const [formState, setFormState] = useState(DEFAULT_FORM);
  const [savedSecretFlags, setSavedSecretFlags] = useState({ smtpPassword: false, twilioAuthToken: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveFeedbackModal, setSaveFeedbackModal] = useState({
    open: false,
    variant: 'success',
    title: '',
    message: '',
  });
  const [resolvedSearchEngine, setResolvedSearchEngine] = useState(null);
  const [notificationSubTab, setNotificationSubTab] = useState('setup');
  const [testEmailTo, setTestEmailTo] = useState('');
  const [testEmailSubject, setTestEmailSubject] = useState('OrderX test email');
  const [testEmailBody, setTestEmailBody] = useState(
    'This is a test email from OrderX notification settings.'
  );
  const [testSmsTo, setTestSmsTo] = useState('');
  const [testSmsBody, setTestSmsBody] = useState(
    'OrderX test SMS — your Twilio configuration is working.'
  );
  const [testEmailLoading, setTestEmailLoading] = useState(false);
  const [testSmsLoading, setTestSmsLoading] = useState(false);
  const [testDeliveryFlash, setTestDeliveryFlash] = useState({ tone: '', message: '' });
  const [testErpPartNumber, setTestErpPartNumber] = useState('');
  const [testErpLoading, setTestErpLoading] = useState(false);
  const [testErpFlash, setTestErpFlash] = useState({ tone: '', message: '' });
  const [cmsMountKey, setCmsMountKey] = useState(0);
  const [checkoutFlowConfig, setCheckoutFlowConfig] = useState(DEFAULT_CHECKOUT_FLOW_CONFIG);

  const updateField = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const patchNotificationEmailByStatus = (statusId, patch) => {
    setFormState((prev) => ({
      ...prev,
      notificationEmailByStatus: {
        ...prev.notificationEmailByStatus,
        [statusId]: {
          ...(prev.notificationEmailByStatus[statusId] || {}),
          ...patch,
        },
      },
    }));
  };

  const loadSettings = async ({ silent = false, refreshProductMatch = true } = {}) => {
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const [resp, resolved] = await Promise.all([
        getOrderGenieSettings(),
        refreshProductMatch ? getOrderGenieProductMatchEngine().catch(() => null) : Promise.resolve(null),
      ]);
      const settings = resp?.settings || resp || {};
      setFormState(buildFormStateFromSettings(settings));
      setSavedSecretFlags({
        smtpPassword: Boolean(settings?.has_smtp_password),
        twilioAuthToken: Boolean(settings?.has_twilio_auth_token),
      });
      if (settings?.checkout_flow_config?.steps) {
        setCheckoutFlowConfig(settings.checkout_flow_config);
      }
      setResolvedSearchEngine(resolved?.product_match || null);
      setCmsMountKey((k) => k + 1);
    } catch (err) {
      if (!silent) {
        setError(err?.message || 'Unable to load account admin settings.');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const section = String(params.get('section') || '').trim().toLowerCase();
    if (!section) return;
    const allowed = new Set(SECTION_ITEMS.map((item) => item.id));
    if (allowed.has(section)) {
      setActiveSection(section);
    }
  }, [location.search]);

  const payload = useMemo(() => ({
    max_concurrent_requests_per_account: normalizeInt(
      formState.maxConcurrentRequestsPerAccount,
      3,
      1,
      20
    ),
    global_active_request_limit: normalizeInt(formState.globalActiveRequestLimit, 3, 1, 20),
    queue_priority_enabled: Boolean(formState.queuePriorityEnabled),
    queue_dispatch_mode: String(formState.queueDispatchMode || 'local_task'),
    queue_channel: String(formState.queueChannel || 'local'),
    queue_weights: {
      urgent: normalizeInt(formState.queueWeightUrgent, 1, 1, 9),
      normal: normalizeInt(formState.queueWeightNormal, 5, 1, 9),
      low: normalizeInt(formState.queueWeightLow, 9, 1, 9),
    },
    queue_admin_users: parseAdminUsers(formState.queueAdminUsersText),
    tabular_only_mode_enabled: Boolean(formState.tabularOnlyModeEnabled),
    tabular_only_completeness_threshold: normalizeFloat(
      formState.tabularOnlyCompletenessThreshold,
      0.95,
      0.5,
      1
    ),
    extraction_ai_call_timeout_sec: normalizeInt(formState.extractionAiCallTimeoutSec, 60, 10, 300),
    extraction_ai_stage_budget_sec: normalizeInt(formState.extractionAiStageBudgetSec, 180, 30, 1200),
    parsing_stall_watchdog_seconds: normalizeInt(formState.parsingStallWatchdogSeconds, 240, 30, 1800),
    tabular_preprocess_enabled: Boolean(formState.tabularPreprocessEnabled),
    tabular_preprocess_sample_rows: normalizeInt(formState.tabularPreprocessSampleRows, 30, 10, 100),
    product_match_engine: String(formState.productMatchEngine || 'solr'),
    product_match_shadow: Boolean(formState.productMatchShadow),
    pricing_engine: ['p21', 'eclipse'].includes(formState.pricingEngine) ? formState.pricingEngine : 'dummy',
    erp_connector: formState.pricingEngine === 'eclipse' ? 'eclipse' : 'p21',
    eclipse_shipto_url: String(formState.eclipseShiptoUrl || '').trim(),
    erp_match_enabled: Boolean(formState.erpMatchEnabled),
    notification_enabled: Boolean(formState.notificationEnabled),
    notification_channel: String(formState.notificationChannel || 'email'),
    notification_review_base_url: String(formState.notificationReviewBaseUrl || '').trim(),
    notification_default_emails: parseCommaLines(formState.notificationDefaultEmailsText),
    notification_default_phones: parseCommaLines(formState.notificationDefaultPhonesText),
    smtp_host: String(formState.smtpHost || '').trim(),
    smtp_port: normalizeInt(formState.smtpPort, 587, 1, 65535),
    smtp_username: String(formState.smtpUsername || '').trim(),
    ...(String(formState.smtpPassword || '').trim() || !savedSecretFlags.smtpPassword
      ? { smtp_password: String(formState.smtpPassword || '').trim() }
      : {}),
    smtp_use_tls: Boolean(formState.smtpUseTls),
    smtp_from_email: String(formState.smtpFromEmail || '').trim(),
    twilio_account_sid: String(formState.twilioAccountSid || '').trim(),
    ...(String(formState.twilioAuthToken || '').trim() || !savedSecretFlags.twilioAuthToken
      ? { twilio_auth_token: String(formState.twilioAuthToken || '').trim() }
      : {}),
    twilio_from_number: String(formState.twilioFromNumber || '').trim(),
    notification_email_subject_template: String(formState.notificationEmailSubjectTemplate || ''),
    notification_email_body_html_template: String(formState.notificationEmailBodyHtmlTemplate || ''),
    notification_email_body_text_template: String(formState.notificationEmailBodyTextTemplate || ''),
    notification_email_by_status: compactNotificationEmailByStatusForApi(formState.notificationEmailByStatus),
    notification_sms_body_template: String(formState.notificationSmsBodyTemplate || ''),
    checkout_flow_config: checkoutFlowConfig,
  }), [formState, checkoutFlowConfig, savedSecretFlags]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const saved = await updateOrderGenieSettings(payload);
      const settings = saved?.settings || saved || {};
      setFormState(buildFormStateFromSettings(settings));
      setSavedSecretFlags({
        smtpPassword: Boolean(settings?.has_smtp_password),
        twilioAuthToken: Boolean(settings?.has_twilio_auth_token),
      });
      if (settings?.checkout_flow_config?.steps) {
        setCheckoutFlowConfig(settings.checkout_flow_config);
      }
      setCmsMountKey((k) => k + 1);
      setSaveFeedbackModal({
        open: true,
        variant: 'success',
        title: 'Settings saved',
        message:
          'Your OrderX account settings were saved successfully. Use Refresh if you also want to update the product matching status panel.',
      });
    } catch (err) {
      setSaveFeedbackModal({
        open: true,
        variant: 'danger',
        title: 'Save failed',
        message: err?.message || 'Failed to save account admin settings.',
      });
    } finally {
      setSaving(false);
    }
  };

  const closeSaveFeedbackModal = () => {
    setSaveFeedbackModal((prev) => ({ ...prev, open: false }));
  };

  return (
    <div className="container-fluid py-3 order-genie-account-admin-page pb-5 mb-5">
      <div className="row g-3">
        <div className="col-12">
          <div>
            <h4 className="mb-1">OrderX Account Admin</h4>
            <div className="text-muted small">
              Manage queue capacity, admin users, dispatch mode, and priority behavior for this account.
            </div>
          </div>
        </div>

        {error && (
          <div className="col-12">
            <div className="alert alert-danger mb-0">{error}</div>
          </div>
        )}

        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-header bg-white border-bottom-0 pb-0 px-2 px-md-3 pt-3">
              <ul
                className="nav nav-tabs flex-nowrap overflow-x-auto border-bottom-0 gap-1 pb-1 mb-0"
                role="tablist"
                aria-label="Account admin settings"
              >
                {SECTION_ITEMS.map((section) => (
                  <li className="nav-item text-nowrap flex-shrink-0" key={section.id}>
                    <button
                      type="button"
                      className={`nav-link ${activeSection === section.id ? 'active' : ''}`}
                      id={`og-admin-tab-${section.id}`}
                      onClick={() => setActiveSection(section.id)}
                      role="tab"
                      aria-selected={activeSection === section.id}
                    >
                      {section.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="card-body pt-2">
              {SECTION_ITEMS.find((s) => s.id === activeSection) && (
                <p className="text-muted small mb-3">
                  {SECTION_ITEMS.find((s) => s.id === activeSection).help}
                </p>
              )}
              {loading && activeSection !== 'readiness' && activeSection !== 'mailbox' && activeSection !== 'checkout-flow' ? (
                <div className="text-muted">Loading settings...</div>
              ) : activeSection === 'readiness' ? (
                <OrderGenieReadiness />
              ) : activeSection === 'checkout-flow' ? (
                <CheckoutFlowAdminSection config={checkoutFlowConfig} onChange={setCheckoutFlowConfig} />
              ) : activeSection === 'mailbox' ? (
                <div className="text-center py-5">
                  <i className="bi bi-envelope-gear display-4 text-muted mb-3 d-block" />
                  <p className="text-muted mb-3">Mailbox configuration has its own dedicated page.</p>
                  <a href="/order-x/mailbox-setup" className="btn btn-primary">
                    <i className="bi bi-box-arrow-up-right me-2" />Go to Mailbox Setup
                  </a>
                </div>
              ) : (
                <div className="row g-3">
                  {(activeSection === 'capacity' || activeSection === 'routing') && (
                    <>
                      <div className="col-12 col-md-6">
                        <label className="form-label">Max concurrent requests per account</label>
                        <input
                          type="number"
                          min={1}
                          max={20}
                          className="form-control"
                          value={formState.maxConcurrentRequestsPerAccount}
                          onChange={(event) => updateField('maxConcurrentRequestsPerAccount', event.target.value)}
                        />
                        <div className="form-text">Primary concurrency limit used by queue orchestration.</div>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label">Global active request limit (legacy fallback)</label>
                        <input
                          type="number"
                          min={1}
                          max={20}
                          className="form-control"
                          value={formState.globalActiveRequestLimit}
                          onChange={(event) => updateField('globalActiveRequestLimit', event.target.value)}
                        />
                        <div className="form-text">Kept for backward compatibility with existing workers.</div>
                      </div>
                    </>
                  )}

                  {(activeSection === 'routing') && (
                    <>
                      <div className="col-12 col-md-6">
                        <label className="form-label">Queue dispatch mode</label>
                        <select
                          className="form-select"
                          value={formState.queueDispatchMode}
                          onChange={(event) => updateField('queueDispatchMode', event.target.value)}
                        >
                          <option value="local_task">local_task</option>
                          <option value="service_bus">service_bus</option>
                        </select>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label">Queue channel</label>
                        <select
                          className="form-select"
                          value={formState.queueChannel}
                          onChange={(event) => updateField('queueChannel', event.target.value)}
                        >
                          <option value="local">local</option>
                          <option value="service_bus">service_bus</option>
                        </select>
                      </div>
                    </>
                  )}

                  {(activeSection === 'search') && (
                    <>
                      <div className="col-12 col-md-6">
                        <label className="form-label">Default product matching engine</label>
                        <select
                          className="form-select"
                          value={formState.productMatchEngine}
                          onChange={(event) => updateField('productMatchEngine', event.target.value)}
                        >
                          <option value="solr">solr</option>
                          <option value="opensearch">opensearch</option>
                          <option value="shadow">shadow (serve primary + compare both)</option>
                        </select>
                        <div className="form-text">
                          Controls which engine OrderX product matching uses by default for this account.
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <div className="form-check form-switch mt-4 pt-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="productMatchShadow"
                            checked={Boolean(formState.productMatchShadow)}
                            onChange={(event) => updateField('productMatchShadow', event.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="productMatchShadow">
                            Enable shadow comparison metrics
                          </label>
                        </div>
                        <div className="form-text">
                          When enabled, matching can collect Solr vs OpenSearch overlap stats for A/B relevance checks.
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label">ERP / pricing engine</label>
                        <select
                          className="form-select"
                          value={formState.pricingEngine}
                          onChange={(event) => updateField('pricingEngine', event.target.value)}
                        >
                          <option value="dummy">dummy</option>
                          <option value="p21">p21</option>
                          <option value="eclipse">eclipse</option>
                        </select>
                        <div className="form-text">
                          Controls which ERP backend OrderX uses for pricing and customer / ship-to lookup. p21:
                          customer search with ship-to and contact lists. eclipse: exact Entity ID lookup via
                          eclipseConnector/shipto (no search) and eclipseConnector/getPrice pricing.
                        </div>
                      </div>
                      {formState.pricingEngine === 'eclipse' ? (
                        <div className="col-12 col-md-6">
                          <label className="form-label">Eclipse shipto URL</label>
                          <input
                            className="form-control"
                            value={formState.eclipseShiptoUrl}
                            placeholder="Derived from the P21 getPrice host when blank"
                            onChange={(event) => updateField('eclipseShiptoUrl', event.target.value)}
                          />
                          <div className="form-text">
                            Optional override for /connector/v1/eclipseConnector/shipto.
                          </div>
                        </div>
                      ) : null}
                      <div className="col-12 col-md-6">
                        <div className="form-check form-switch mt-4 pt-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="erpMatchEnabled"
                            checked={Boolean(formState.erpMatchEnabled)}
                            onChange={(event) => updateField('erpMatchEnabled', event.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="erpMatchEnabled">
                            Enable ERP match fallback
                          </label>
                        </div>
                        <div className="form-text">
                          When enabled, unmatched items can be confirmed against the configured ERP pricing engine.
                        </div>
                      </div>
                      {formState.pricingEngine === 'dummy' ? (
                        <div className="col-12">
                          <div className="alert alert-warning small mb-0">
                            ERP match fallback and this test only take effect when ERP / pricing engine is set to p21 or eclipse — on
                            dummy, every part number will appear to match.
                          </div>
                        </div>
                      ) : null}
                      <div className="col-12">
                        <div className="card border shadow-sm">
                          <div className="card-header bg-white py-3">
                            <div className="fw-semibold">Test ERP match</div>
                            <div className="small text-muted mt-1">
                              Type a part number to check whether it resolves to a priced ERP match, without running a
                              full order through the matching pipeline.
                            </div>
                            <div className="small text-muted mt-1">
                              Tests your last-saved settings — save changes above first if you just edited them.
                            </div>
                          </div>
                          <div className="card-body">
                            <div className="row g-2 align-items-end">
                              <div className="col-12 col-md-6">
                                <label className="form-label small" htmlFor="ogTestErpPartNumber">
                                  Part number
                                </label>
                                <input
                                  id="ogTestErpPartNumber"
                                  type="text"
                                  className="form-control form-control-sm"
                                  value={testErpPartNumber}
                                  onChange={(e) => setTestErpPartNumber(e.target.value)}
                                  placeholder="e.g. ABC-123"
                                />
                              </div>
                              <div className="col-12 col-md-auto">
                                <button
                                  type="button"
                                  className="btn btn-primary btn-sm"
                                  disabled={testErpLoading || !String(testErpPartNumber || '').trim()}
                                  onClick={async () => {
                                    const code = String(testErpPartNumber || '').trim();
                                    setTestErpFlash({ tone: '', message: '' });
                                    setTestErpLoading(true);
                                    try {
                                      const res = await getOrderGenieErpPricesBulk([code]);
                                      const prices = res?.prices || {};
                                      const price = prices[code];
                                      if (price === undefined || price === null) {
                                        setTestErpFlash({
                                          tone: 'neutral',
                                          message: `No ERP match found for "${code}".`,
                                        });
                                      } else if (!Number.isFinite(Number(price))) {
                                        setTestErpFlash({
                                          tone: 'danger',
                                          message: 'Unexpected response from ERP price lookup.',
                                        });
                                      } else {
                                        setTestErpFlash({
                                          tone: 'success',
                                          message: `ERP match found: ${code} — $${Number(price).toFixed(2)}`,
                                        });
                                      }
                                    } catch (err) {
                                      setTestErpFlash({
                                        tone: 'danger',
                                        message: err?.message || 'Failed to test ERP match.',
                                      });
                                    } finally {
                                      setTestErpLoading(false);
                                    }
                                  }}
                                >
                                  {testErpLoading ? 'Testing…' : 'Test ERP match'}
                                </button>
                              </div>
                            </div>
                            {testErpFlash.message ? (
                              <div
                                className={`alert mt-3 mb-0 small ${
                                  testErpFlash.tone === 'success'
                                    ? 'alert-success'
                                    : testErpFlash.tone === 'danger'
                                      ? 'alert-danger'
                                      : 'alert-secondary'
                                }`}
                                role="status"
                              >
                                {testErpFlash.message}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="card border bg-light-subtle">
                          <div className="card-body py-3">
                            <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                              <div className="fw-semibold">Resolved Engine Status (read-only)</div>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary"
                                onClick={loadSettings}
                                disabled={loading || saving}
                              >
                                Refresh status
                              </button>
                            </div>
                            {resolvedSearchEngine ? (
                              <div className="row g-2 small">
                                <div className="col-12 col-md-3">
                                  <div className="text-muted">Resolved engine</div>
                                  <div className="fw-semibold">{String(resolvedSearchEngine.engine || 'unknown')}</div>
                                </div>
                                <div className="col-12 col-md-3">
                                  <div className="text-muted">Shadow enabled</div>
                                  <div className="fw-semibold">{resolvedSearchEngine.shadow_enabled ? 'true' : 'false'}</div>
                                </div>
                                <div className="col-12 col-md-3">
                                  <div className="text-muted">OpenSearch index</div>
                                  <div className="fw-semibold text-break">{String(resolvedSearchEngine.opensearch_index || '-')}</div>
                                </div>
                                <div className="col-12 col-md-3">
                                  <div className="text-muted">OpenSearch base URL</div>
                                  <div className="fw-semibold text-break">{String(resolvedSearchEngine.opensearch_base_url || '-')}</div>
                                </div>
                              </div>
                            ) : (
                              <div className="text-muted small">No resolved engine status available yet.</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {(activeSection === 'priority') && (
                    <>
                      <div className="col-12">
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="queuePriorityEnabled"
                            checked={Boolean(formState.queuePriorityEnabled)}
                            onChange={(event) => updateField('queuePriorityEnabled', event.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="queuePriorityEnabled">
                            Enable queue priority controls
                          </label>
                        </div>
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="form-label">Urgent weight</label>
                        <input
                          type="number"
                          min={1}
                          max={9}
                          className="form-control"
                          value={formState.queueWeightUrgent}
                          onChange={(event) => updateField('queueWeightUrgent', event.target.value)}
                        />
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="form-label">Normal weight</label>
                        <input
                          type="number"
                          min={1}
                          max={9}
                          className="form-control"
                          value={formState.queueWeightNormal}
                          onChange={(event) => updateField('queueWeightNormal', event.target.value)}
                        />
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="form-label">Low weight</label>
                        <input
                          type="number"
                          min={1}
                          max={9}
                          className="form-control"
                          value={formState.queueWeightLow}
                          onChange={(event) => updateField('queueWeightLow', event.target.value)}
                        />
                      </div>
                    </>
                  )}

                  {(activeSection === 'access') && (
                    <div className="col-12">
                      <label className="form-label">Queue admin users</label>
                      <textarea
                        className="form-control"
                        rows={10}
                        value={formState.queueAdminUsersText}
                        onChange={(event) => updateField('queueAdminUsersText', event.target.value)}
                        placeholder={'Enter one user id per line, or comma-separated.\nExample:\nswamy_local\nops_admin'}
                      />
                      <div className="form-text">
                        Only these user IDs can set <code>urgent</code> priority.
                      </div>
                    </div>
                  )}

                  {(activeSection === 'extraction') && (
                    <>
                      <div className="col-12">
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="tabularOnlyModeEnabled"
                            checked={Boolean(formState.tabularOnlyModeEnabled)}
                            onChange={(event) => updateField('tabularOnlyModeEnabled', event.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="tabularOnlyModeEnabled">
                            Enable tabular-only extraction when rows are complete
                          </label>
                        </div>
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="form-label">Tabular completeness threshold</label>
                        <input
                          type="number"
                          step="0.01"
                          min={0.5}
                          max={1}
                          className="form-control"
                          value={formState.tabularOnlyCompletenessThreshold}
                          onChange={(event) => updateField('tabularOnlyCompletenessThreshold', event.target.value)}
                        />
                        <div className="form-text">When extraction ratio meets this value, AI extraction is skipped.</div>
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="form-label">AI call timeout (seconds)</label>
                        <input
                          type="number"
                          min={10}
                          max={300}
                          className="form-control"
                          value={formState.extractionAiCallTimeoutSec}
                          onChange={(event) => updateField('extractionAiCallTimeoutSec', event.target.value)}
                        />
                      </div>
                      <div className="col-12 col-md-4">
                        <label className="form-label">AI stage budget (seconds)</label>
                        <input
                          type="number"
                          min={30}
                          max={1200}
                          className="form-control"
                          value={formState.extractionAiStageBudgetSec}
                          onChange={(event) => updateField('extractionAiStageBudgetSec', event.target.value)}
                        />
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label">Parsing stall watchdog (seconds)</label>
                        <input
                          type="number"
                          min={30}
                          max={1800}
                          className="form-control"
                          value={formState.parsingStallWatchdogSeconds}
                          onChange={(event) => updateField('parsingStallWatchdogSeconds', event.target.value)}
                        />
                        <div className="form-text">Fails stuck parsing runs so requests do not stay running forever.</div>
                      </div>
                      <div className="col-12">
                        <hr className="my-1" />
                      </div>
                      <div className="col-12">
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="tabularPreprocessEnabled"
                            checked={Boolean(formState.tabularPreprocessEnabled)}
                            onChange={(event) => updateField('tabularPreprocessEnabled', event.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="tabularPreprocessEnabled">
                            Enable tabular AI pre-processing (Excel/CSV first rows)
                          </label>
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="form-label">Tabular sample rows</label>
                        <input
                          type="number"
                          min={10}
                          max={100}
                          className="form-control"
                          value={formState.tabularPreprocessSampleRows}
                          onChange={(event) => updateField('tabularPreprocessSampleRows', event.target.value)}
                        />
                        <div className="form-text">First N rows sent to AI for tabular pre-processing.</div>
                      </div>
                    </>
                  )}

                  {activeSection === 'notifications' && (() => {
                    const ch = String(formState.notificationChannel || 'email').toLowerCase();
                    const usesEmail = ch === 'email' || ch === 'both';
                    const usesSms = ch === 'sms' || ch === 'both';
                    const notificationsLocked = !formState.notificationEnabled;
                    return (
                      <>
                        <div className="col-12">
                          <ul
                            className="nav nav-tabs flex-nowrap overflow-x-auto mb-2 border-bottom gap-1"
                            role="tablist"
                            aria-label="Notification configuration"
                          >
                            {NOTIFICATION_SUB_TABS.map((tab) => (
                              <li className="nav-item text-nowrap flex-shrink-0" key={tab.id}>
                                <button
                                  type="button"
                                  className={`nav-link ${notificationSubTab === tab.id ? 'active' : ''}`}
                                  onClick={() => setNotificationSubTab(tab.id)}
                                  role="tab"
                                  aria-selected={notificationSubTab === tab.id}
                                >
                                  {tab.label}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {notificationSubTab === 'setup' && (
                          <>
                        <div className="col-12">
                          <div className="alert alert-light border mb-3 py-3">
                            <div className="fw-semibold mb-1">How this works</div>
                            <p className="small text-muted mb-0">
                              After someone submits a request in OrderX, you can send them a message with a
                              read-only review link and a short summary. Users normally enter their own email or phone in
                              the widget; the fallback lists below are only used when they leave those fields blank.
                              Use the <strong>Email template</strong> and <strong>SMS template</strong> tabs to customize
                              message content, or leave templates empty for built-in defaults.
                            </p>
                          </div>
                        </div>

                        <div className="col-12">
                          <div className="card border shadow-sm mb-3">
                            <div className="card-body">
                              <div className="d-flex flex-wrap align-items-start justify-content-between gap-2">
                                <div className="form-check form-switch mb-0">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id="notificationEnabled"
                                    checked={Boolean(formState.notificationEnabled)}
                                    onChange={(event) => updateField('notificationEnabled', event.target.checked)}
                                  />
                                  <label className="form-check-label fw-semibold" htmlFor="notificationEnabled">
                                    Turn on request notifications
                                  </label>
                                </div>
                                {formState.notificationEnabled ? (
                                  <span className="badge rounded-pill text-bg-success align-self-center">Active</span>
                                ) : (
                                  <span className="badge rounded-pill text-bg-secondary align-self-center">Off</span>
                                )}
                              </div>
                              <p className="small text-muted mb-0 mt-2">
                                When off, no messages are sent and the settings below are ignored until you enable this
                                again.
                              </p>
                            </div>
                          </div>
                        </div>

                        <fieldset
                          className="col-12 border-0 m-0 p-0"
                          disabled={notificationsLocked}
                        >
                          <div className="row g-3">
                            <div className="col-12 col-xl-6">
                              <div className="card border h-100 shadow-sm">
                                <div className="card-header bg-white py-3">
                                  <div className="fw-semibold">Delivery</div>
                                  <div className="small text-muted mt-1">
                                    Choose email, SMS, or both. SMTP and Twilio sections appear when relevant. Customize
                                    message wording on the Email template and SMS template tabs.
                                  </div>
                                </div>
                                <div className="card-body">
                                  <label className="form-label" htmlFor="notificationChannel">
                                    Channels
                                  </label>
                                  <select
                                    id="notificationChannel"
                                    className="form-select"
                                    value={formState.notificationChannel}
                                    onChange={(event) => updateField('notificationChannel', event.target.value)}
                                  >
                                    <option value="email">Email only</option>
                                    <option value="sms">SMS (text) only — Twilio</option>
                                    <option value="both">Email and SMS</option>
                                  </select>
                                  <label className="form-label mt-3" htmlFor="notificationReviewBaseUrl">
                                    Review link base URL
                                  </label>
                                  <input
                                    id="notificationReviewBaseUrl"
                                    type="url"
                                    className="form-control"
                                    value={formState.notificationReviewBaseUrl}
                                    onChange={(event) => updateField('notificationReviewBaseUrl', event.target.value)}
                                    placeholder="https://api.yourcompany.com"
                                  />
                                  <div className="form-text">
                                    Public links look like{' '}
                                    <code className="small">…/api/v1/orderx/public/review/&lt;token&gt;</code>
                                    . If empty, your OrderX service URL from settings is used.
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="col-12 col-xl-6">
                              <div className="card border h-100 shadow-sm">
                                <div className="card-header bg-white py-3">
                                  <div className="fw-semibold">Fallback recipients</div>
                                  <div className="small text-muted mt-1">
                                    Optional backup addresses when the submitter does not enter an email or phone in the
                                    widget.
                                  </div>
                                </div>
                                <div className="card-body">
                                  <label className="form-label" htmlFor="notificationDefaultEmails">
                                    Fallback emails
                                  </label>
                                  <textarea
                                    id="notificationDefaultEmails"
                                    className="form-control"
                                    rows={4}
                                    value={formState.notificationDefaultEmailsText}
                                    onChange={(event) => updateField('notificationDefaultEmailsText', event.target.value)}
                                    placeholder={'ops@company.com\nmanager@company.com'}
                                    disabled={!usesEmail}
                                  />
                                  {!usesEmail ? (
                                    <div className="form-text">Not used while delivery is SMS-only.</div>
                                  ) : (
                                    <div className="form-text">One per line or comma-separated.</div>
                                  )}
                                  <label className="form-label mt-3" htmlFor="notificationDefaultPhones">
                                    Fallback phone numbers
                                  </label>
                                  <textarea
                                    id="notificationDefaultPhones"
                                    className="form-control"
                                    rows={4}
                                    value={formState.notificationDefaultPhonesText}
                                    onChange={(event) => updateField('notificationDefaultPhonesText', event.target.value)}
                                    placeholder="+15551234567"
                                    disabled={!usesSms}
                                  />
                                  {!usesSms ? (
                                    <div className="form-text">Not used while delivery is email-only.</div>
                                  ) : (
                                    <div className="form-text">Use E.164 format (e.g. +1…). One per line or commas.</div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {usesEmail ? (
                              <div className="col-12">
                                <div className="card border shadow-sm">
                                  <div className="card-header bg-white py-3 d-flex flex-wrap align-items-center gap-2">
                                    <span className="fw-semibold">Email server (SMTP)</span>
                                    <span className="badge rounded-pill bg-light text-dark border">Required for email</span>
                                  </div>
                                  <div className="card-body">
                                    <div className="row g-3">
                                      <div className="col-12 col-md-6">
                                        <label className="form-label" htmlFor="smtpHost">Server host</label>
                                        <input
                                          id="smtpHost"
                                          type="text"
                                          className="form-control"
                                          value={formState.smtpHost}
                                          onChange={(event) => updateField('smtpHost', event.target.value)}
                                          placeholder="smtp.office365.com"
                                        />
                                      </div>
                                      <div className="col-6 col-md-3">
                                        <label className="form-label" htmlFor="smtpPort">Port</label>
                                        <input
                                          id="smtpPort"
                                          type="number"
                                          min={1}
                                          max={65535}
                                          className="form-control"
                                          value={formState.smtpPort}
                                          onChange={(event) => updateField('smtpPort', event.target.value)}
                                        />
                                      </div>
                                      <div className="col-6 col-md-3 d-flex align-items-end">
                                        <div className="form-check form-switch mb-2">
                                          <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="smtpUseTls"
                                            checked={Boolean(formState.smtpUseTls)}
                                            onChange={(event) => updateField('smtpUseTls', event.target.checked)}
                                          />
                                          <label className="form-check-label small" htmlFor="smtpUseTls">
                                            Use STARTTLS
                                          </label>
                                        </div>
                                      </div>
                                      <div className="col-12 col-md-6">
                                        <label className="form-label" htmlFor="smtpUsername">Username</label>
                                        <input
                                          id="smtpUsername"
                                          type="text"
                                          className="form-control"
                                          value={formState.smtpUsername}
                                          onChange={(event) => updateField('smtpUsername', event.target.value)}
                                          autoComplete="off"
                                        />
                                      </div>
                                      <div className="col-12 col-md-6">
                                        <label className="form-label" htmlFor="smtpPassword">Password</label>
                                        <input
                                          id="smtpPassword"
                                          type="password"
                                          className="form-control"
                                          value={formState.smtpPassword}
                                          onChange={(event) => updateField('smtpPassword', event.target.value)}
                                          autoComplete="new-password"
                                          placeholder={
                                            savedSecretFlags.smtpPassword
                                              ? '•••••••••••••••• (saved password on file — enter a new one to replace it)'
                                              : undefined
                                          }
                                        />
                                      </div>
                                      <div className="col-12 col-md-6">
                                        <label className="form-label" htmlFor="smtpFromEmail">From address</label>
                                        <input
                                          id="smtpFromEmail"
                                          type="email"
                                          className="form-control"
                                          value={formState.smtpFromEmail}
                                          onChange={(event) => updateField('smtpFromEmail', event.target.value)}
                                          placeholder="notifications@yourcompany.com"
                                        />
                                        <div className="form-text">Shown as the sender on outbound mail.</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : null}

                            {usesSms ? (
                              <div className="col-12">
                                <div className="card border shadow-sm">
                                  <div className="card-header bg-white py-3 d-flex flex-wrap align-items-center gap-2">
                                    <span className="fw-semibold">SMS (Twilio)</span>
                                    <span className="badge rounded-pill bg-light text-dark border">Required for SMS</span>
                                  </div>
                                  <div className="card-body">
                                    <div className="row g-3">
                                      <div className="col-12 col-md-4">
                                        <label className="form-label" htmlFor="twilioAccountSid">Account SID</label>
                                        <input
                                          id="twilioAccountSid"
                                          type="text"
                                          className="form-control font-monospace small"
                                          value={formState.twilioAccountSid}
                                          onChange={(event) => updateField('twilioAccountSid', event.target.value)}
                                          autoComplete="off"
                                        />
                                      </div>
                                      <div className="col-12 col-md-4">
                                        <label className="form-label" htmlFor="twilioAuthToken">Auth token</label>
                                        <input
                                          id="twilioAuthToken"
                                          type="password"
                                          className="form-control"
                                          value={formState.twilioAuthToken}
                                          onChange={(event) => updateField('twilioAuthToken', event.target.value)}
                                          autoComplete="new-password"
                                          placeholder={
                                            savedSecretFlags.twilioAuthToken
                                              ? '•••••••••••••••• (saved token on file — enter a new one to replace it)'
                                              : undefined
                                          }
                                        />
                                      </div>
                                      <div className="col-12 col-md-4">
                                        <label className="form-label" htmlFor="twilioFromNumber">From number</label>
                                        <input
                                          id="twilioFromNumber"
                                          type="text"
                                          className="form-control"
                                          value={formState.twilioFromNumber}
                                          onChange={(event) => updateField('twilioFromNumber', event.target.value)}
                                          placeholder="+15551234567"
                                        />
                                        <div className="form-text">Must be a Twilio-enabled sender ID.</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : null}

                          </div>
                        </fieldset>
                          </>
                        )}

                        {notificationSubTab === 'email-template' && (
                          <div className="col-12">
                            <fieldset className="border-0 m-0 p-0" disabled={notificationsLocked}>
                              {!usesEmail ? (
                                <div className="alert alert-info mb-0">
                                  Set <strong>Channels</strong> to <strong>Email only</strong> or{' '}
                                  <strong>Email and SMS</strong> on the Setup tab to edit email templates.
                                </div>
                              ) : (
                                <OrderGenieEmailTemplateEditor
                                  variables={NOTIFICATION_TEMPLATE_HINTS}
                                  globalSubject={formState.notificationEmailSubjectTemplate}
                                  globalHtml={formState.notificationEmailBodyHtmlTemplate}
                                  globalText={formState.notificationEmailBodyTextTemplate}
                                  onGlobalChange={updateField}
                                  byStatus={formState.notificationEmailByStatus}
                                  onByStatusChange={patchNotificationEmailByStatus}
                                  disabled={notificationsLocked}
                                  cmsMountKey={cmsMountKey}
                                />
                              )}
                            </fieldset>
                          </div>
                        )}

                        {notificationSubTab === 'sms-template' && (
                          <div className="col-12">
                            <fieldset className="border-0 m-0 p-0" disabled={notificationsLocked}>
                              {!usesSms ? (
                                <div className="alert alert-info mb-0">
                                  Set <strong>Channels</strong> to <strong>SMS (text) only</strong> or{' '}
                                  <strong>Email and SMS</strong> on the Setup tab to edit the SMS template.
                                </div>
                              ) : (
                                <>
                                  <p className="text-muted small mb-3">
                                    Optional override for text messages. Leave blank to use the built-in default.
                                  </p>
                                  <div className="small text-muted mb-3">
                                    <span className="me-1">Insert any of:</span>
                                    {NOTIFICATION_TEMPLATE_HINTS.map(({ token, label }) => (
                                      <span key={token} className="d-inline-block me-1 mb-1" title={label}>
                                        <code className="small px-2 py-1 bg-light border rounded">{token}</code>
                                      </span>
                                    ))}
                                  </div>
                                  <label className="form-label" htmlFor="notificationSmsBodyTemplate">
                                    SMS body
                                  </label>
                                  <textarea
                                    id="notificationSmsBodyTemplate"
                                    className="form-control font-monospace small"
                                    rows={5}
                                    value={formState.notificationSmsBodyTemplate}
                                    onChange={(event) => updateField('notificationSmsBodyTemplate', event.target.value)}
                                    placeholder="Blank = default text message"
                                  />
                                  <div className="form-text">Keep SMS concise; long messages may be truncated.</div>
                                </>
                              )}
                            </fieldset>
                          </div>
                        )}

                        {notificationSubTab === 'test' && (
                        <div className="col-12">
                          <div className="card border shadow-sm">
                            <div className="card-header bg-white py-3">
                              <div className="fw-semibold">Test delivery</div>
                              <div className="small text-muted mt-1">
                                Send a sample email or text using the SMTP and Twilio settings on this page. Save your
                                changes first if you edited connection details. You do not need to turn on “request
                                notifications” above to run a test.
                              </div>
                            </div>
                            <div className="card-body">
                              <div className="row g-4">
                                <div className="col-12 col-lg-6">
                                  <div className="border rounded p-3 h-100 bg-light">
                                    <div className="fw-semibold small mb-3">Test email (SMTP)</div>
                                    <label className="form-label small" htmlFor="ogTestEmailTo">
                                      Send to
                                    </label>
                                    <input
                                      id="ogTestEmailTo"
                                      type="email"
                                      className="form-control form-control-sm mb-2"
                                      value={testEmailTo}
                                      onChange={(e) => setTestEmailTo(e.target.value)}
                                      placeholder="you@company.com"
                                      autoComplete="email"
                                    />
                                    <label className="form-label small" htmlFor="ogTestEmailSubject">
                                      Subject
                                    </label>
                                    <input
                                      id="ogTestEmailSubject"
                                      type="text"
                                      className="form-control form-control-sm mb-2"
                                      value={testEmailSubject}
                                      onChange={(e) => setTestEmailSubject(e.target.value)}
                                    />
                                    <label className="form-label small" htmlFor="ogTestEmailBody">
                                      Message (plain text)
                                    </label>
                                    <textarea
                                      id="ogTestEmailBody"
                                      className="form-control form-control-sm mb-3"
                                      rows={4}
                                      value={testEmailBody}
                                      onChange={(e) => setTestEmailBody(e.target.value)}
                                    />
                                    <button
                                      type="button"
                                      className="btn btn-primary btn-sm"
                                      disabled={testEmailLoading || !String(testEmailTo || '').trim()}
                                      onClick={async () => {
                                        setTestDeliveryFlash({ tone: '', message: '' });
                                        setTestEmailLoading(true);
                                        try {
                                          const res = await testOrderGenieNotificationEmail({
                                            to_email: String(testEmailTo || '').trim(),
                                            subject: testEmailSubject,
                                            body_text: testEmailBody,
                                          });
                                          setTestDeliveryFlash({
                                            tone: 'success',
                                            message: res?.detail || 'Test email sent.',
                                          });
                                        } catch (err) {
                                          setTestDeliveryFlash({
                                            tone: 'danger',
                                            message: err?.message || 'Failed to send test email.',
                                          });
                                        } finally {
                                          setTestEmailLoading(false);
                                        }
                                      }}
                                    >
                                      {testEmailLoading ? 'Sending…' : 'Send test email'}
                                    </button>
                                  </div>
                                </div>
                                <div className="col-12 col-lg-6">
                                  <div className="border rounded p-3 h-100 bg-light">
                                    <div className="fw-semibold small mb-3">Test SMS (Twilio)</div>
                                    <label className="form-label small" htmlFor="ogTestSmsTo">
                                      Send to (E.164)
                                    </label>
                                    <input
                                      id="ogTestSmsTo"
                                      type="tel"
                                      className="form-control form-control-sm mb-2"
                                      value={testSmsTo}
                                      onChange={(e) => setTestSmsTo(e.target.value)}
                                      placeholder="+15551234567"
                                      autoComplete="tel"
                                    />
                                    <label className="form-label small" htmlFor="ogTestSmsBody">
                                      Message
                                    </label>
                                    <textarea
                                      id="ogTestSmsBody"
                                      className="form-control form-control-sm mb-3"
                                      rows={4}
                                      value={testSmsBody}
                                      onChange={(e) => setTestSmsBody(e.target.value)}
                                    />
                                    <button
                                      type="button"
                                      className="btn btn-primary btn-sm"
                                      disabled={testSmsLoading || !String(testSmsTo || '').trim()}
                                      onClick={async () => {
                                        setTestDeliveryFlash({ tone: '', message: '' });
                                        setTestSmsLoading(true);
                                        try {
                                          const res = await testOrderGenieNotificationSms({
                                            to_phone: String(testSmsTo || '').trim(),
                                            body: testSmsBody,
                                          });
                                          setTestDeliveryFlash({
                                            tone: 'success',
                                            message: res?.detail || 'Test SMS sent.',
                                          });
                                        } catch (err) {
                                          setTestDeliveryFlash({
                                            tone: 'danger',
                                            message: err?.message || 'Failed to send test SMS.',
                                          });
                                        } finally {
                                          setTestSmsLoading(false);
                                        }
                                      }}
                                    >
                                      {testSmsLoading ? 'Sending…' : 'Send test SMS'}
                                    </button>
                                  </div>
                                </div>
                              </div>
                              {testDeliveryFlash.message ? (
                                <div
                                  className={`alert alert-${testDeliveryFlash.tone === 'success' ? 'success' : 'danger'} mt-3 mb-0 small`}
                                  role="status"
                                >
                                  {testDeliveryFlash.message}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className="order-genie-account-admin-actions position-fixed bottom-0 start-0 end-0 border-top bg-white shadow-sm py-2 px-2 px-md-3"
        style={{
          zIndex: 1030,
          paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))',
          display: (activeSection === 'readiness' || activeSection === 'mailbox') ? 'none' : undefined,
          // mailbox redirects away so no save bar needed there either
        }}
        role="region"
        aria-label="Save and refresh actions"
      >
        <div className="container-fluid d-flex flex-wrap align-items-center justify-content-end gap-2">
          {(saving || loading) && (
            <span className="small text-muted me-auto text-truncate" style={{ maxWidth: '55%' }}>
              {saving ? 'Saving…' : 'Loading…'}
            </span>
          )}
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => loadSettings()}
            disabled={loading || saving}
          >
            Refresh
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={saveSettings}
            disabled={loading || saving}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {saveFeedbackModal.open ? (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
          aria-labelledby="og-save-feedback-title"
          style={{ backgroundColor: 'rgba(33, 37, 41, 0.5)', zIndex: 1040 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeSaveFeedbackModal();
            }
          }}
        >
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable px-2">
            <div className="modal-content border-0 shadow">
              <div
                className={`modal-header border-0 ${
                  saveFeedbackModal.variant === 'success' ? 'bg-success-subtle' : 'bg-danger-subtle'
                }`}
              >
                <h5 className="modal-title" id="og-save-feedback-title">
                  {saveFeedbackModal.title}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={closeSaveFeedbackModal}
                />
              </div>
              <div className="modal-body pt-2">
                <p className="mb-0">{saveFeedbackModal.message}</p>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button type="button" className="btn btn-primary" onClick={closeSaveFeedbackModal}>
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default OrderGenieAccountAdmin;
